import { Map } from 'immutable';
import is from 'is_js';

import {
  FETCH_PROGRESS_TEST_REQUEST,
  FETCH_PROGRESS_TEST_SUCCESS,
  FETCH_PROGRESS_TEST_FAILURE,
  SUBMIT_PROGRESS_TEST_RESULTS_REQUEST,
  RETAKE_PROGRESS_TEST_REQUEST,
  RETAKE_PROGRESS_TEST_SUCCESS,
} from './actions';
import ProgressTest from './model';

import { ApiResponseHelper } from 'omni-common-ui';

const toMap = (arr, key) =>
  new Map(arr.reduce((obj, item) => {
    // eslint-disable-next-line no-param-reassign
    obj[item[key]] = item;  // stringifies keys!
    return obj;
  }, {}));

export default function progressTests(state = new Map({}), action = {}) {
  const { payload, groupId, progressReportId, name: progressTestName } = action;
  switch (action.type) {
    case FETCH_PROGRESS_TEST_REQUEST:
    case SUBMIT_PROGRESS_TEST_RESULTS_REQUEST:
    case RETAKE_PROGRESS_TEST_REQUEST:
      return state.set(action.progressTestId, ApiResponseHelper.create({
        loading: true,
      }));
    case FETCH_PROGRESS_TEST_SUCCESS:
      return state.set(action.progressTestId, ApiResponseHelper.create(
        createProgressTest(payload, groupId, progressReportId, progressTestName)));
    case RETAKE_PROGRESS_TEST_SUCCESS:
      return addReTakeStateToMap(state, action);
    case FETCH_PROGRESS_TEST_FAILURE:
      return state.set(action.progressTestId, ApiResponseHelper.create(action.error));
    default:
      return state;
  }
}

export function createProgressTest(payload, groupId, progressReportId, progressTestName) {
  const studentProgressTestResults = payload.studentProgressTestResults || [];
  return ProgressTest.create(
    Object.assign({}, payload, {
      groupId,
      progressReportId,
      name: progressTestName,
      skills: payload.skills || [],
      studentProgressTestResults: studentProgressTestResults.map((result) => {
        const res = result; // grr - eslint

        res.skillResults = createStudentProgressTestSkillResults(result, payload.skills);
        res.totalScore = createStudentProgressTestTotalScore(result, payload.skills);
        res.previousTotalScore =
            createStudentProgressTestTotalScore(result, payload.skills, 'previousResult');

        return res;
      }),
    })
  );
}

function createStudentProgressTestSkillResults(result, skills) {
  return result.skillResults.map((skillResult) => {
    const progressTestSkill = skills.find((skill) =>
      skill.skillId === skillResult.skillId);
    if (is.undefined(progressTestSkill)) {
      throw new Error(`skill ${skillResult.skillId} could not be found in the content map`);
    }
    if (progressTestSkill.isRubric) {
      const skillAbilitiesMap = toMap(progressTestSkill.abilities, 'abilityId');
      const rubricScoreAndFullMark =
          findScoreAndFullMarkForRubricResult(skillResult, progressTestSkill, skillAbilitiesMap);
      const previousRubricScoreAndFullMark =
          findScoreAndFullMarkForRubricResult(skillResult, progressTestSkill, skillAbilitiesMap,
            'previousResultAbilityId');
      // eslint-disable-next-line no-param-reassign
      skillResult = Object.assign({}, skillResult, {
        currentResult: rubricScoreAndFullMark.score,
        previousResult: previousRubricScoreAndFullMark.score,
        fullMark: rubricScoreAndFullMark.fullMark,
      });
    }
    if (is.number(skillResult.fullMark)) {
      progressTestSkill.fullMark = skillResult.fullMark;
    }
    return skillResult;
  });
}

// it could be argued that the backend should be doing this stuff
function findScoreAndFullMarkForRubricResult(rubricResult, progressTestSkill, skillAbilitiesMap,
      abilityIdKey = 'currentResultAbilityId') {
  const fallbackAbilityIdKey = 'currentResultAbilityId';
  // fullMark is the highest ability score * number of subSkills in the rubric skill
  // eslint-disable-next-line no-confusing-arrow
  const fullMark = progressTestSkill.abilities.reduce((max, ability) =>
    ability.point > max ? ability.point : max
  , 0) * progressTestSkill.subSkills.length;
  // score is the sum of the points of abilities in a student's sub skill result set
  // (I know...)
  let isAnyNull = false;
  const score = rubricResult.subSkillResults.reduce((totalSubSkillScore, subSkillResult = {}) => {
    const isResultAvailable = subSkillResult && is.not.null(subSkillResult[abilityIdKey]);
    const scoredPointsForSubSkill = isResultAvailable ?
      (skillAbilitiesMap.get(`${subSkillResult[abilityIdKey]}`) || {}).point :
      (skillAbilitiesMap.get(`${subSkillResult[fallbackAbilityIdKey]}`) || {}).point || 0;
    isAnyNull = isAnyNull || is.null(subSkillResult[abilityIdKey]);
    return totalSubSkillScore + scoredPointsForSubSkill;
  }, 0);
  return { score: isAnyNull ? null : score, fullMark };
}

function createStudentProgressTestTotalScore(result, progressTestSkills,
    scoreKey = 'currentResult') {
  const fallbackScoreKey = 'currentResult';
  const skillResults = toMap(result.skillResults, 'skillId');
  let skillScorePercentages = new Map();
  let output = new Map({ skillScorePercentages });
  let totalScore = 0;
  let SkillCount = 0;
  let isAnyNA = false;
  progressTestSkills.forEach((skill) => {
    const skillResult = skillResults.get(skill.skillId.toString());
    const isResultAvailable = is.object(skillResult) &&
        is.number(skillResult[scoreKey]);
    const isFallbackResultAvailable = is.object(skillResult) &&
        is.number(skillResult[fallbackScoreKey]);
    if (! skillResult.isAbandoned) {
      if (isResultAvailable || isFallbackResultAvailable) {
        const score =
            isResultAvailable ? skillResult[scoreKey] : skillResult[fallbackScoreKey];
        const baseNum = score * 100;
        const percentage = Math.min(100, baseNum / skill.fullMark);
        const percentageRounded = Math.round(percentage);
        skillScorePercentages = skillScorePercentages.set(`${skill.skillId}`, percentageRounded);
        output = output.set('skillScorePercentages', skillScorePercentages);
        totalScore += percentage;
        SkillCount += 1;
      } else {
        isAnyNA = true;
      }
    }
  });
  if (isAnyNA) {
    return output.set('isNotApplicable', true);
  }
  if (totalScore === 0) {
    // no divide by zero!
    return output.set('percentage', 0);
  }
  const perc = Math.round(totalScore / SkillCount);
  return Number.isNaN(perc) ?
      output.set('isEmpty', true) :
      output.set('percentage', perc);
}

function addReTakeStateToMap(state, { progressTestId, studentId }) {
  let newState = state;
  newState = newState.set('progressTestResetted', { progressTestId, studentId });
  return newState;
}




// WEBPACK FOOTER //
// ./data/ProgressTests/reducer.js